import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fmtIDR, todayISO } from "@/lib/format";
import { Save, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Which COA codes are editable as opening balances
const OPENING_ACCOUNTS = [
  { code: "1101", label: "Kas", side: "debit" },
  { code: "1102", label: "Bank", side: "debit" },
  { code: "1103", label: "Piutang Usaha", side: "debit" },
  { code: "1104", label: "Persediaan BBM Pertalite", side: "debit" },
  { code: "1105", label: "Persediaan BBM Dexlite", side: "debit" },
  { code: "1106", label: "Persediaan BBM Pertamax", side: "debit" },
  { code: "1107", label: "Persediaan BBM Pertamax Turbo", side: "debit" },
  { code: "1201", label: "Tanah", side: "debit" },
  { code: "1202", label: "Bangunan", side: "debit" },
  { code: "1203", label: "Peralatan & Mesin", side: "debit" },
  { code: "1204", label: "Kendaraan", side: "debit" },
  { code: "2101", label: "Utang Usaha", side: "credit" },
  { code: "2102", label: "Utang Pajak", side: "credit" },
  { code: "2201", label: "Utang Jangka Panjang", side: "credit" },
];

const NeracaAwal = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [openingDate, setOpeningDate] = useState(todayISO());
  const [balances, setBalances] = useState({});

  useEffect(() => {
    api.get("/opening-balances").then((r) => {
      const d = r.data;
      if (d.opening_date) setOpeningDate(d.opening_date);
      setBalances(d.balances || {});
    });
  }, []);

  const debits = OPENING_ACCOUNTS.filter((a) => a.side === "debit");
  const credits = OPENING_ACCOUNTS.filter((a) => a.side === "credit");
  const sumDebit = debits.reduce((s, a) => s + parseFloat(balances[a.code] || 0), 0);
  const sumCredit = credits.reduce((s, a) => s + parseFloat(balances[a.code] || 0), 0);
  const plug = sumDebit - sumCredit; // → Modal Disetor (3101)

  const save = async () => {
    try {
      await api.put("/opening-balances", { opening_date: openingDate, balances });
      toast.success("Neraca Awal tersimpan");
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const set = (code, v) => setBalances((prev) => ({ ...prev, [code]: v }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Neraca Awal</h1>
          <p className="text-sm text-slate-500 mt-1">Impor saldo awal dari catatan manual — akan otomatis dibukukan sebagai jurnal Saldo Awal</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Tanggal Awal Neraca</Label>
            <Input type="date" value={openingDate} onChange={(e) => setOpeningDate(e.target.value)} disabled={!isAdmin} />
          </div>
          {isAdmin && <Button onClick={save} data-testid="save-opening-button" className="bg-[#E30613] hover:bg-[#C8040F]"><Save className="w-4 h-4 mr-2" />Simpan</Button>}
        </div>
      </div>

      <Card className="border-blue-200 bg-blue-50/40">
        <CardContent className="p-4 flex gap-3 items-start">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            Selisih Debit dan Kredit akan otomatis dibukukan ke akun <b>3101 Modal Disetor</b> sebagai penyeimbang.
            Neraca Awal ini menjadi baseline sebelum semua transaksi tercatat di aplikasi.
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base font-bold">Saldo Debit (Aset)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {debits.map((a) => (
              <div key={a.code} className="flex items-center gap-3">
                <div className="w-12 font-mono-fig text-xs text-slate-500">{a.code}</div>
                <div className="flex-1 text-sm">{a.label}</div>
                <Input type="number" step="0.01" className="w-40 text-right font-mono-fig"
                  value={balances[a.code] || ""} disabled={!isAdmin}
                  onChange={(e) => set(a.code, e.target.value)} data-testid={`opening-${a.code}`} />
              </div>
            ))}
            <div className="flex justify-between pt-3 border-t font-bold">
              <span>Total Debit</span><span className="font-mono-fig text-slate-900">{fmtIDR(sumDebit)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base font-bold">Saldo Kredit (Liabilitas)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {credits.map((a) => (
              <div key={a.code} className="flex items-center gap-3">
                <div className="w-12 font-mono-fig text-xs text-slate-500">{a.code}</div>
                <div className="flex-1 text-sm">{a.label}</div>
                <Input type="number" step="0.01" className="w-40 text-right font-mono-fig"
                  value={balances[a.code] || ""} disabled={!isAdmin}
                  onChange={(e) => set(a.code, e.target.value)} data-testid={`opening-${a.code}`} />
              </div>
            ))}
            <div className="flex justify-between pt-3 border-t font-bold">
              <span>Total Kredit</span><span className="font-mono-fig text-slate-900">{fmtIDR(sumCredit)}</span>
            </div>
            <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 flex justify-between items-center">
              <div>
                <div className="text-xs text-violet-700 font-semibold">→ Auto post ke 3101 Modal Disetor</div>
                <div className="text-xs text-slate-500">Debit − Kredit</div>
              </div>
              <div className={`font-mono-fig font-bold ${plug >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {fmtIDR(plug)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NeracaAwal;
